/**
 * Всплывающая подсказка с содержимым, а не строкой.
 *
 * Атрибут `title` умеет только текст, появляется через секунду и не читается
 * с клавиатуры. Здесь нужно другое: описание канала — это несколько полей,
 * которые надо видеть рядом и сразу.
 *
 * Рисуется порталом в body с координатами от элемента. Не «чтобы красиво»:
 * ряд вкладок прокручивается по горизонтали (`overflow-x: auto`), а он
 * обрезает всё, что выходит за края, — подсказка внутри него была бы срезана
 * ровно у крайних вкладок, то есть чаще всего.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const GAP = 8;

export function Hint({
  children,
  content,
  className,
}: {
  children: ReactNode;
  content: ReactNode;
  className?: string;
}): React.JSX.Element {
  const hostRef = useRef<HTMLSpanElement>(null);
  const [box, setBox] = useState<{ left: number; top: number } | null>(null);
  const id = useId();

  const show = (): void => {
    const el = hostRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setBox({ left: r.left + r.width / 2, top: r.bottom + GAP });
  };
  const hide = (): void => setBox(null);

  // Прокрутка и изменение размера уводят подсказку от того, к чему она
  // относится. Проще убрать, чем гнаться: она всё равно живёт секунды.
  useEffect(() => {
    if (!box) return;
    const off = (): void => hide();
    window.addEventListener('scroll', off, true);
    window.addEventListener('resize', off);
    const esc = (e: KeyboardEvent): void => { if (e.key === 'Escape') hide(); };
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('scroll', off, true);
      window.removeEventListener('resize', off);
      window.removeEventListener('keydown', esc);
    };
  }, [box]);

  return (
    <>
      <span
        ref={hostRef}
        className={className}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        tabIndex={0}
        aria-describedby={box ? id : undefined}
      >
        {children}
      </span>
      {box && typeof document !== 'undefined' && createPortal(
        <div
          id={id}
          role="tooltip"
          className="hint"
          style={{ left: box.left, top: box.top }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  );
}
